import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Alert, Button } from "reactstrap";

import { changeSidebarTheme } from "../../slices/thunks";
import {
  isShellVisualPreviewActive,
  persistShellVisualPreview,
  readShellVisualPreview,
  SHELL_VISUAL_PREVIEW_EVENT,
  ShellVisualPreviewVariant,
  sidebarThemeForPreview,
} from "../../helpers/shell_visual_preview";

/**
 * Sticky notice while a Visual Lab shell preview is active (session/localStorage).
 * Does not change the product default until PO promotes a variant.
 */
const ShellVisualPreviewBanner = () => {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const [variant, setVariant] = useState<ShellVisualPreviewVariant>(() =>
    readShellVisualPreview()
  );

  useEffect(() => {
    const sync = () => setVariant(readShellVisualPreview());
    window.addEventListener(SHELL_VISUAL_PREVIEW_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SHELL_VISUAL_PREVIEW_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!isShellVisualPreviewActive(variant)) {
    return null;
  }

  const reset = () => {
    persistShellVisualPreview("current");
    dispatch(changeSidebarTheme(sidebarThemeForPreview("current")));
    setVariant("current");
  };

  return (
    <Alert
      color="warning"
      className="rounded-0 border-0 mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2"
      role="status"
    >
      <span className="small mb-0">
        {t("studio.visualLab.banner.active", {
          variant: t(`studio.visualLab.variants.${variant}.title`),
        })}
      </span>
      <span className="d-flex flex-wrap gap-2">
        <Button
          tag={Link}
          to="/settings/visual-lab"
          color="warning"
          outline
          size="sm"
          className="bg-white"
        >
          {t("studio.visualLab.banner.openLab")}
        </Button>
        <Button color="dark" size="sm" onClick={reset}>
          {t("studio.visualLab.banner.reset")}
        </Button>
      </span>
    </Alert>
  );
};

export default ShellVisualPreviewBanner;
